import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import ContactSection from "@/components/ContactSection";
import HeyflowBanner from "@/components/HeyflowBanner";
import ProvenExpertBadge from "@/components/ProvenExpertBadge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { 
  TrendingUp,
  Home, 
  DollarSign, 
  Heart, 
  CheckCircle, 
  ArrowLeft,
  Phone,
  Mail,
  Calculator,
  Users,
  Award,
  Clock,
  Shield,
  Percent
} from "lucide-react";
import janineHardiImage from "@/assets/janine-hardi.jpg";
import { Link } from "react-router-dom";
import teilverkaufImage from "@/assets/teilverkauf.png";

const Teilverkauf = () => {

  const benefits = [
    {
      icon: DollarSign,
      title: "Sofortige Auszahlung",
      description: "Unmittelbare hohe Einmalzahlung nach Vertragsabschluss"
    },
    {
      icon: Percent,
      title: "Flexibel wählbarer Anteil",
      description: "Sie bestimmen selbst, wie viel Prozent verkauft werden"
    },
    {
      icon: Heart,
      title: "Erbe bleibt teilweise erhalten", 
      description: "Restwert geht später an die Erben über"
    },
    {
      icon: Home,
      title: "Wohnrecht bleibt",
      description: "Lebenslang in der vertrauten Immobilie wohnen"
    }
  ];

  const examples = [
    {
      title: "Einfamilienhaus Hamburg",
      value: "800.000 €",
      percentage: "40%",
      payout: "320.000 €",
      remaining: "480.000 €",
      details: "Wohnrecht gesichert, Restwert bleibt im Eigentum"
    },
    {
      title: "Eigentumswohnung Düsseldorf", 
      value: "400.000 €",
      percentage: "30%",
      payout: "120.000 €",
      remaining: "280.000 €",
      details: "Erben sichern den Restwert"
    },
    {
      title: "Haus im Süden Deutschlands",
      value: "600.000 €", 
      percentage: "50%",
      payout: "300.000 €",
      remaining: "300.000 €",
      details: "Finanzierung von Pflege- und Reisekosten"
    }
  ];

  const faqItems = [
    {
      question: "Muss ich Miete zahlen?",
      answer: "Nein, aber es fällt ein Nutzungsentgelt für den verkauften Anteil an. Dieses orientiert sich am Marktwert und ist deutlich geringer als eine Marktmiete."
    },
    {
      question: "Kann ich den verkauften Anteil später zurückkaufen?",
      answer: "Ja, viele Modelle sehen diese Möglichkeit vor. Die Rückkaufbedingungen werden im Vertrag festgelegt."
    },
    {
      question: "Was passiert mit dem Restanteil nach meinem Tod?",
      answer: "Dieser Anteil geht vollständig an die Erben über. So bleibt ein Teil des Familienvermögens erhalten."
    },
    {
      question: "Wie hoch ist die Einmalzahlung?",
      answer: "Das hängt vom Immobilienwert und vom gewählten Verkaufsanteil ab. Bei einer 500.000 € Immobilie und 40% Verkauf erhalten Sie 200.000 €."
    },
    {
      question: "Ist mein Wohnrecht abgesichert?",
      answer: "Ja, es wird notariell im Grundbuch eingetragen und ist damit rechtlich vollständig abgesichert."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Teilverkauf: Teil des Hauses verkaufen & wohnen bleiben </title>
        <meta 
          name="description" 
          content="Teilverkauf: Verkaufen Sie einen Teil Ihres Hauses, bleiben wohnen & erhalten sofortige Liquidität. ✓ Wohnrecht ✓ Erbe teilweise erhalten ✓ Kostenlose Beratung"
        />
        <meta name="keywords" content="teilverkauf, haus verkaufen und wohnen bleiben, verkaufen aber wohnen bleiben, wohnrecht, immobilie verrenten, kapital aus der immobilie, eigenheimverrentung" />
        <link rel="canonical" href="https://eigenheimverrentung.de/teilverkauf" />
        <meta name="robots" content="index, follow" />
        <meta name="googlebot" content="index, follow" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content="Teilverkauf: Teil des Hauses verkaufen & wohnen bleiben" />
        <meta property="og:description" content="Verkaufen Sie einen Teil Ihres Hauses, bleiben wohnen und erhalten sofortige Liquidität. Kostenlose Beratung." />
        <meta property="og:url" content="https://eigenheimverrentung.de/teilverkauf" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Teilverkauf: Teil des Hauses verkaufen & wohnen bleiben" />
        <meta name="twitter:description" content="Verkaufen Sie einen Teil Ihrer Immobilie, bleiben Sie wohnen und erhalten sofortige Liquidität." />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "Teilverkauf – Verkauf eines Teils der Immobilie bei Erhalt des Wohnrechts",
            "description": "Kompletter Ratgeber zum Teilverkauf: Verkaufen Sie einen Teil Ihrer Immobilie, bleiben wohnen und erhalten sofortige Liquidität.",
            "author": {
              "@type": "Organization",
              "name": "eigenheimverrentung.de"
            },
            "publisher": {
              "@type": "Organization",
              "name": "eigenheimverrentung.de", 
              "url": "https://eigenheimverrentung.de"
            },
            "datePublished": new Date().toISOString(),
            "dateModified": new Date().toISOString(),
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": "https://eigenheimverrentung.de/teilverkauf"
            }
          })}
        </script>
        
        {/* FAQ Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Muss ich Miete zahlen?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Nein, aber es fällt ein Nutzungsentgelt für den verkauften Anteil an. Dieses orientiert sich am Marktwert und ist deutlich geringer als eine Marktmiete."
                }
              },
              {
                "@type": "Question",
                "name": "Kann ich den verkauften Anteil später zurückkaufen?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Ja, viele Modelle sehen diese Möglichkeit vor. Die Rückkaufbedingungen werden im Vertrag festgelegt."
                }
              },
              {
                "@type": "Question",
                "name": "Was passiert mit dem Restanteil nach meinem Tod?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Dieser Anteil geht vollständig an die Erben über. So bleibt ein Teil des Familienvermögens erhalten."
                }
              },
              {
                "@type": "Question",
                "name": "Wie hoch ist die Einmalzahlung?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Das hängt vom Immobilienwert und vom gewählten Verkaufsanteil ab. Bei einer 500.000 € Immobilie und 40% Verkauf erhalten Sie 200.000 €."
                }
              },
              {
                "@type": "Question",
                "name": "Ist mein Wohnrecht abgesichert?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Ja, es wird notariell im Grundbuch eingetragen und ist damit rechtlich vollständig abgesichert."
                }
              }
            ]
          })}
        </script>

        {/* Organization & Local Business Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "RentePlusImmobilie - Eigenheimverrentung",
            "url": "https://eigenheimverrentung.de",
            "telephone": "+49-89-628246446",
            "email": "info@renteplusimmobilie.de",
            "address": {
              "@type": "PostalAddress",
              "addressCountry": "DE"
            },
            "areaServed": "Deutschland",
            "serviceType": ["Leibrente", "Wohnrecht", "Nießbrauchrecht", "Teilverkauf", "Rückmiete", "Seniorenfinanzierung"],
            "description": "Kostenlose Beratung zur Eigenheimverrentung mit Teilverkauf - Teil der Immobilie verkaufen und wohnen bleiben."
          })}
        </script>

        {/* BreadcrumbList Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Startseite",
                "item": "https://eigenheimverrentung.de/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Teilverkauf",
                "item": "https://eigenheimverrentung.de/teilverkauf"
              }
            ]
          })}
        </script>

        {/* HowTo Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            "name": "Wie funktioniert ein Teilverkauf?",
            "description": "Schritt-für-Schritt Anleitung zum Teilverkauf - Teil des Hauses verkaufen und wohnen bleiben",
            "totalTime": "P30D",
            "step": [
              {
                "@type": "HowToStep",
                "position": 1,
                "name": "Kostenlose Erstberatung",
                "text": "Neutrale Beratung zu allen Verrentungsmodellen"
              },
              {
                "@type": "HowToStep",
                "position": 2,
                "name": "Immobilienbewertung",
                "text": "Professionelle Bewertung zur Festlegung des fairen Teilkaufpreises"
              },
              {
                "@type": "HowToStep",
                "position": 3,
                "name": "Angebotsvergleich",
                "text": "Gegenüberstellung der Angebote verschiedener seriöser Teilkauf-Anbieter"
              },
              {
                "@type": "HowToStep",
                "position": 4,
                "name": "Vertragsabschluss",
                "text": "Notarieller Kaufvertrag mit Eintragung des Wohnrechts im Grundbuch"
              },
              {
                "@type": "HowToStep",
                "position": 5,
                "name": "Auszahlung",
                "text": "Sofortige Auszahlung des vereinbarten Teilkaufpreises"
              }
            ]
          })}
        </script>

        {/* Service Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Teilverkauf",
            "name": "Teilverkauf - Flexibilität und sofortige Liquidität",
            "description": "Verkaufen Sie einen Teil Ihrer Immobilie bei Erhalt des Wohnrechts. Flexibel wählbarer Anteil mit sofortiger Auszahlung.",
            "provider": {
              "@type": "LocalBusiness",
              "name": "RentePlusImmobilie - Eigenheimverrentung",
              "url": "https://eigenheimverrentung.de",
              "telephone": "+49-89-628246446",
              "email": "info@renteplusimmobilie.de"
            },
            "areaServed": {
              "@type": "Country",
              "name": "Deutschland"
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "Immobilieneigentümer die Kapital benötigen aber Miteigentümer bleiben möchten"
            },
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Teilverkauf Leistungen",
              "itemListElement": [
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Flexibler Verkaufsanteil",
                    "description": "Sie bestimmen selbst, wie viel Prozent verkauft werden (30-50%)"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Sofortige Einmalzahlung",
                    "description": "Hohe Einmalzahlung direkt nach Vertragsabschluss"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Rückkaufoption",
                    "description": "Möglichkeit den verkauften Anteil später zurückzukaufen"
                  }
                }
              ]
            },
            "url": "https://eigenheimverrentung.de/teilverkauf"
          })}
        </script>
      </Helmet>
      
      <div className="min-h-screen bg-background">
        <Header />
        
        <main>
        {/* Breadcrumb Navigation */}
        <section className="py-4 bg-muted/30 border-b">
          <div className="container-comfortable">
            <nav className="flex items-center gap-2 text-sm text-muted-foreground">
              <Link to="/" className="hover:text-primary transition-colors">
                Startseite
              </Link>
              <span>/</span>
              <span className="text-primary font-medium">Teilverkauf</span>
            </nav>
          </div>
        </section>

        {/* Hero Section */}
        <section className="py-16 md:py-24 bg-gradient-subtle">
          <div className="container-comfortable">
            <div className="max-w-4xl mx-auto text-center">
              <Badge variant="secondary" className="mb-6 text-base px-4 py-2">
                Kostenlose Beratung
              </Badge>
              <h1 className="text-4xl md:text-6xl font-bold text-primary mb-6 text-balance">
                Teilverkauf – Flexibilität und sofortige Liquidität
              </h1>
              <p className="text-xl md:text-2xl text-muted-foreground mb-8 text-balance">
                Verkaufen Sie einen Teil Ihrer Immobilie bei Erhalt des Wohnrechts und erhalten sofortige Auszahlung
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" className="text-xl px-8" onClick={() => window.open('https://calendly.com/renteplusimmobilie/20min', '_blank')}>
                  <Calculator className="h-5 w-5 mr-2" />
                  Kostenloses Telefongespräch
                </Button>
                <Button variant="outline" size="lg" className="text-xl px-8" onClick={() => window.open('tel:089628246446', '_self')}>
                  <Phone className="h-5 w-5 mr-2" />
                  089 62 82 46 46
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16 md:py-24">
          <div className="container-comfortable">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
                Ihre Vorteile beim Teilverkauf
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Der Teilverkauf bietet Ihnen die perfekte Balance zwischen sofortiger Liquidität und langfristigem Vermögenserhalt
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((benefit, index) => (
                <Card key={index} className="card-elevated text-center">
                  <CardHeader className="pb-4">
                    <div className="bg-accent/10 p-4 rounded-xl w-fit mx-auto mb-4">
                      <benefit.icon className="h-8 w-8 text-accent" />
                    </div>
                    <CardTitle className="text-xl">{benefit.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{benefit.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Image Section */}
        <section className="py-12 md:py-16">
          <div className="container-comfortable">
            <div className="max-w-2xl mx-auto text-center">
              <img 
                src={teilverkaufImage} 
                alt="Teilverkauf - Haus teilweise verkaufen und wohnen bleiben" 
                className="w-full max-w-md mx-auto rounded-xl shadow-lg"
              />
            </div>
          </div>
        </section>

        {/* What is Teilverkauf Section */}
        <section className="py-16 md:py-24 bg-muted/30">
          <div className="container-comfortable">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-8 text-center">
                Was ist ein Teilverkauf?
              </h2>
              
              <div className="prose prose-lg max-w-none text-muted-foreground">
                <p className="text-xl leading-relaxed mb-6">
                  Beim Teilverkauf veräußert der Eigentümer einen bestimmten Anteil seiner Immobilie – zum Beispiel 30%, 40% oder 50%. Den Rest behält er. Im Gegenzug erhält er eine sofortige Einmalzahlung für den verkauften Anteil und bleibt gleichzeitig Miteigentümer.
                </p>
                
                <div className="bg-card p-6 rounded-xl border mb-8">
                  <h3 className="text-2xl font-bold text-primary mb-4">So funktioniert der Teilverkauf:</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-accent mt-1 flex-shrink-0" />
                      <span>Sie verkaufen nur einen Teil Ihrer Immobilie (z.B. 40%)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-accent mt-1 flex-shrink-0" />
                      <span>Sie erhalten sofort eine hohe Einmalzahlung</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-accent mt-1 flex-shrink-0" />
                      <span>Sie bleiben Miteigentümer mit lebenslangem Wohnrecht</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-accent mt-1 flex-shrink-0" />
                      <span>Ein monatliches Nutzungsentgelt für den verkauften Anteil ist zu zahlen</span>
                    </li>
                  </ul>
                </div>

                <h3 className="text-2xl font-bold text-primary mb-4">Abgrenzung zu anderen Modellen</h3>
                <div className="grid gap-4">
                  <div className="bg-card p-4 rounded-lg border">
                    <h4 className="font-semibold text-primary mb-2">Teilverkauf vs. Leibrente</h4>
                    <p>Bei der Leibrente gibt es monatliche Rentenzahlungen, beim Teilverkauf eine sofortige Einmalzahlung und Sie bleiben Miteigentümer.</p>
                  </div>
                  <div className="bg-card p-4 rounded-lg border">
                    <h4 className="font-semibold text-primary mb-2">Teilverkauf vs. Umkehrhypothek</h4>
                    <p>Bei der Umkehrhypothek entsteht eine Kreditverbindlichkeit, beim Teilverkauf verkaufen Sie real einen Anteil ohne Schulden.</p>
                  </div>
                  <div className="bg-card p-4 rounded-lg border">
                    <h4 className="font-semibold text-primary mb-2">Teilverkauf vs. Rückmiete</h4>
                    <p>Bei der Rückmiete wird die Immobilie komplett verkauft, beim Teilverkauf bleiben Sie Miteigentümer.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Target Group Section */}
        <section className="py-16 md:py-24">
          <div className="container-comfortable">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-8 text-center">
                Für wen eignet sich der Teilverkauf?
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="card-elevated">
                  <CardHeader>
                    <CardTitle className="text-xl text-primary flex items-center gap-2">
                      <Users className="h-6 w-6 text-accent" />
                      Senioren ab 60 Jahren
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">Die Kapital aus ihrer Immobilie lösen wollen, ohne komplett zu verkaufen</p>
                  </CardContent>
                </Card>

                <Card className="card-elevated">
                  <CardHeader>
                    <CardTitle className="text-xl text-primary flex items-center gap-2">
                      <Heart className="h-6 w-6 text-accent" />
                      Familienbewusste Eigentümer
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">Die das Erbe teilweise sichern wollen, da ein Anteil der Immobilie im Besitz bleibt</p>
                  </CardContent>
                </Card>

                <Card className="card-elevated">
                  <CardHeader>
                    <CardTitle className="text-xl text-primary flex items-center gap-2">
                      <DollarSign className="h-6 w-6 text-accent" />
                      Bei Liquiditätsbedarf
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">Für Pflege, Unterstützung der Familie oder andere wichtige Investitionen</p>
                  </CardContent>
                </Card>

                <Card className="card-elevated">
                  <CardHeader>
                    <CardTitle className="text-xl text-primary flex items-center gap-2">
                      <TrendingUp className="h-6 w-6 text-accent" />
                      Flexibilitäts-Liebhaber
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">Die später erneut verkaufen oder den Anteil zurückkaufen möchten</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Example Calculations */}
        <section className="py-16 md:py-24 bg-muted/30">
          <div className="container-comfortable">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
                Beispielrechnungen & Praxisfälle
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Konkrete Beispiele zeigen, wie sich der Teilverkauf in verschiedenen Situationen auswirkt
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {examples.map((example, index) => (
                <Card key={index} className="card-elevated">
                  <CardHeader>
                    <CardTitle className="text-xl text-primary">{example.title}</CardTitle>
                    <div className="text-3xl font-bold text-accent">{example.value}</div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Verkaufsanteil:</span>
                      <span className="font-semibold text-xl">{example.percentage}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Auszahlung:</span>
                      <span className="font-bold text-2xl text-primary">{example.payout}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Verbleibt im Besitz:</span>
                      <span className="font-semibold text-accent">{example.remaining}</span>
                    </div>
                    <Separator />
                    <p className="text-sm text-muted-foreground">{example.details}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-16 md:py-24">
          <div className="container-comfortable">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
                Der Ablauf bei renteplusimmobilie.de
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                In 5 einfachen Schritten zu Ihrem Teilverkauf
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              <Card className="card-elevated text-center">
                <CardHeader>
                  <div className="bg-accent/10 p-4 rounded-xl w-fit mx-auto mb-4">
                    <Users className="h-8 w-8 text-accent" />
                  </div>
                  <CardTitle className="text-lg">1. Kostenlose Erstberatung</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm">Neutrale Beratung zu allen Verrentungsmodellen</p>
                </CardContent>
              </Card>

              <Card className="card-elevated text-center">
                <CardHeader>
                  <div className="bg-accent/10 p-4 rounded-xl w-fit mx-auto mb-4">
                    <Calculator className="h-8 w-8 text-accent" />
                  </div>
                  <CardTitle className="text-lg">2. Immobilienbewertung</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm">Professionelle Bewertung für exakten Marktwert</p>
                </CardContent>
              </Card>

              <Card className="card-elevated text-center">
                <CardHeader>
                  <div className="bg-accent/10 p-4 rounded-xl w-fit mx-auto mb-4">
                    <Percent className="h-8 w-8 text-accent" />
                  </div>
                  <CardTitle className="text-lg">3. Festlegung des Anteils</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm">Sie entscheiden den prozentualen Verkaufsanteil</p>
                </CardContent>
              </Card>

              <Card className="card-elevated text-center">
                <CardHeader>
                  <div className="bg-accent/10 p-4 rounded-xl w-fit mx-auto mb-4">
                    <Shield className="h-8 w-8 text-accent" />
                  </div>
                  <CardTitle className="text-lg">4. Notarielle Absicherung</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm">Rechtliche Absicherung aller Rechte und Pflichten</p>
                </CardContent>
              </Card>

              <Card className="card-elevated text-center">
                <CardHeader>
                  <div className="bg-accent/10 p-4 rounded-xl w-fit mx-auto mb-4">
                    <DollarSign className="h-8 w-8 text-accent" />
                  </div>
                  <CardTitle className="text-lg">5. Auszahlung</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm">Sofortige Liquidität nach Vertragsabschluss</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 md:py-24 bg-muted/30">
          <div className="container-comfortable">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
                Häufige Fragen zum Teilverkauf
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Die wichtigsten Antworten zu Ihren Fragen rund um den Teilverkauf
              </p>
            </div>

            <div className="max-w-4xl mx-auto space-y-6">
              {faqItems.map((item, index) => (
                <Card key={index} className="card-elevated">
                  <CardHeader>
                    <CardTitle className="text-xl text-primary">{item.question}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground leading-relaxed">{item.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Why Us Section */}
        <section className="py-16 md:py-24">
          <div className="container-comfortable">
            <div className="text-center mb-12">
              {/* Founder Image */}
              <div className="mb-8">
                <img 
                  src={janineHardiImage} 
                  alt="Janine Hardi - Gründerin von RentePlusImmobilie" 
                  className="w-32 h-32 md:w-40 md:h-40 rounded-full object-cover mx-auto shadow-lg"
                />
                <p className="text-sm text-muted-foreground mt-3">Janine Hardi, Gründerin von RentePlusImmobilie</p>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4 text-center">
                Warum RentePlusImmobilie
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Ihre Vorteile bei der Zusammenarbeit mit unseren Teilverkauf-Experten
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <Card className="card-elevated">
                <CardHeader>
                  <CardTitle className="text-xl text-primary flex items-center gap-2">
                    <Users className="h-6 w-6 text-accent" />
                    Unabhängige Experten
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">Das Team berät neutral zu allen Verrentungsmodellen ohne Verkaufsdruck</p>
                </CardContent>
              </Card>

              <Card className="card-elevated">
                <CardHeader>
                  <CardTitle className="text-xl text-primary flex items-center gap-2">
                    <Shield className="h-6 w-6 text-accent" />
                    Kostenlose Beratung
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">Keine Gebühren, keine versteckten Kosten – Transparenz von Anfang an</p>
                </CardContent>
              </Card>

              <Card className="card-elevated">
                <CardHeader>
                  <CardTitle className="text-xl text-primary flex items-center gap-2">
                    <Award className="h-6 w-6 text-accent" />
                    Individuelle Lösungen
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">Jeder Vertrag wird maßgeschneidert und an Ihre Bedürfnisse angepasst</p>
                </CardContent>
              </Card>

              <Card className="card-elevated">
                <CardHeader>
                  <CardTitle className="text-xl text-primary flex items-center gap-2">
                    <Heart className="h-6 w-6 text-accent" />
                    Vertrauen & Erfahrung
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">Zahlreiche erfolgreiche Teilverkäufe in ganz Deutschland</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-24 bg-gradient-subtle">
          <div className="container-comfortable">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">
                Mit Teilverkauf flexibel und liquide bleiben
              </h2>
              <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
                Der Teilverkauf ist eine ideale Lösung für Eigentümer, die Liquidität benötigen, aber weiterhin in ihrer Immobilie wohnen möchten und dabei flexibel für die Zukunft bleiben wollen.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <Button size="lg" className="text-xl px-8" onClick={() => window.open('https://calendly.com/renteplusimmobilie/20min', '_blank')}>
                  <Calculator className="h-5 w-5 mr-2" />
                  Kostenloses Telefongespräch
                </Button>
                <Button variant="accent" size="lg" className="text-xl px-8" onClick={() => window.open('tel:089628246446', '_self')}>
                  <Phone className="h-5 w-5 mr-2" />
                  089 62 82 46 46
                </Button>
                <Button variant="outline" size="lg" className="text-xl px-8" onClick={() => window.open('mailto:info@renteplusimmobilie.de', '_self')}>
                  <Mail className="h-5 w-5 mr-2" />
                  E-Mail senden
                </Button>
              </div>
              <p className="text-lg text-muted-foreground">
                Kostenlose Beratung • Unabhängig • Deutschlandweit
              </p>
            </div>
          </div>
        </section>

        <ProvenExpertBadge />
        <ContactSection />
        <HeyflowBanner />
      </main>
    </div>
    </>
  );
};

export default Teilverkauf;